import { useRef, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { completePublicMaraSignature, getPublicMaraSignatureInfo } from "utils/maraService";

export default function MaraSignaturePage() {
  const { publicToken } = useParams();
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const infoQuery = useQuery({
    queryKey: ["public-mara-signature-info", publicToken],
    queryFn: () => getPublicMaraSignatureInfo(publicToken),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const completeMutation = useMutation({
    mutationFn: () =>
      completePublicMaraSignature({ publicToken, imageBase64: canvasRef.current.toDataURL("image/png") }),
    onSuccess: () => {
      toast.success("Signature submitted");
      infoQuery.refetch();
    },
    onError: (err) => toast.error(err?.response?.data?.errorMessage || "Failed to submit signature"),
  });

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handlePointerDown = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const context = canvas.getContext("2d");
    context.strokeStyle = "#0f172a";
    context.lineWidth = 3;
    context.lineCap = "round";
    context.beginPath();
    context.moveTo(event.clientX - rect.left, event.clientY - rect.top);
    drawingRef.current = true;
  };

  const handlePointerMove = (event) => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const context = canvas.getContext("2d");
    context.lineTo(event.clientX - rect.left, event.clientY - rect.top);
    context.stroke();
    setHasDrawn(true);
  };

  const status = infoQuery.data?.status;
  const isPending = status === "PENDING";
  const isDone = status === "COMPLETED";
  const isExpired = status === "EXPIRED";

  return (
    <>
      <Backdrop open={infoQuery.isLoading} sx={{ color: "#fff", zIndex: 1300 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className="min-h-screen bg-slate-100 py-10 px-4">
        <Box sx={{ maxWidth: 640, mx: "auto" }}>
          <Card sx={{ borderRadius: 5 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" fontWeight={800}>
                Signature required
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                {infoQuery.data?.organizationName} needs {infoQuery.data?.agentName || "your"} signature
                on file to include on MARA-regulated documents sent to clients on your behalf.
              </Typography>

              {isDone && (
                <Alert severity="success" sx={{ mt: 3 }}>
                  Thank you — your signature has been recorded and this link is now closed.
                </Alert>
              )}
              {isExpired && <Alert severity="error" sx={{ mt: 3 }}>This signing link has expired.</Alert>}

              {isPending && (
                <Stack spacing={2} sx={{ mt: 3 }}>
                  <Alert severity="info">
                    Draw your signature below exactly as it should appear on documents, then submit.
                    No document is shown here — this link only captures your signature.
                  </Alert>
                  <Box sx={{ border: "1px solid #cbd5e1", borderRadius: 3, overflow: "hidden", backgroundColor: "#fff" }}>
                    <canvas
                      ref={canvasRef}
                      width={560}
                      height={220}
                      style={{ width: "100%", display: "block", touchAction: "none" }}
                      onPointerDown={handlePointerDown}
                      onPointerMove={handlePointerMove}
                      onPointerUp={() => (drawingRef.current = false)}
                      onPointerLeave={() => (drawingRef.current = false)}
                    />
                  </Box>
                  <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                    <Button onClick={clearCanvas} disabled={completeMutation.isLoading}>
                      Clear
                    </Button>
                    <Button
                      variant="contained"
                      disabled={!hasDrawn || completeMutation.isLoading}
                      onClick={() => completeMutation.mutate()}
                    >
                      {completeMutation.isLoading ? "Submitting…" : "Submit signature"}
                    </Button>
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Box>
      </div>
    </>
  );
}
